import type { SelfProfile } from '@coderush/shared';
import type { Session } from '@supabase/supabase-js';
import { useQueryClient } from '@tanstack/react-query';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';

import { useMe } from '../api/queries.js';
import { supabase } from '../lib/supabase.js';

interface AuthContextValue {
  session: Session | null;
  profile: SelfProfile | null;
  /** セッション復元が終わるまで true。ここで描き分けないと画面がちらつく。 */
  loading: boolean;
  isStaff: boolean;
  isAdmin: boolean;
  signIn: (email: string, password: string) => Promise<void>;
  signUp: (email: string, password: string, username: string) => Promise<void>;
  signOut: () => Promise<void>;
  resetPassword: (email: string) => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null>(null);
  const [restoring, setRestoring] = useState(true);
  const queryClient = useQueryClient();

  // 直前に見えていたユーザー。undefined は「まだ一度も確定していない」。
  const knownUserId = useRef<string | null | undefined>(undefined);

  useEffect(() => {
    let cancelled = false;

    /**
     * キャッシュを捨てるのは**利用者が変わったときだけ**。
     *
     * 以前は onAuthStateChange のたびに queryClient.clear() を呼んでいたが、
     * これには 2 つ問題があった。
     *
     *   1. clear() はキャッシュから query を削除するだけで、
     *      マウント済みの observer に再取得を促さない。
     *      画面が表示された直後に INITIAL_SESSION が届くと、
     *      その画面の query が消えたまま復活せず、永久に「読み込み中」で固まる。
     *      発生はタイミング依存なので、再現しにくい形で表に出る（実際に踏んだ）。
     *   2. トークンの自動更新でも発火するため、
     *      同じ利用者のままキャッシュ全体が無駄に破棄されていた。
     *
     * resetQueries() は初期状態へ戻したうえで、表示中の query を refetch する。
     * 「前の利用者のデータを残さない」と「画面が止まらない」を両立できる。
     */
    const syncIdentity = (next: Session | null): void => {
      const nextUserId = next?.user.id ?? null;
      const previous = knownUserId.current;
      knownUserId.current = nextUserId;
      if (previous !== undefined && previous !== nextUserId) {
        void queryClient.resetQueries();
      }
    };

    void supabase.auth.getSession().then(({ data }) => {
      if (!cancelled) {
        syncIdentity(data.session);
        setSession(data.session);
        setRestoring(false);
      }
    });

    const { data: subscription } = supabase.auth.onAuthStateChange((_event, next) => {
      syncIdentity(next);
      setSession(next);
    });

    return () => {
      cancelled = true;
      subscription.subscription.unsubscribe();
    };
  }, [queryClient]);

  const { data: profile, isLoading: profileLoading } = useMe(session !== null);

  const signIn = useCallback(async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) {
      throw new Error(translateAuthError(error.message));
    }
  }, []);

  const signUp = useCallback(async (email: string, password: string, username: string) => {
    const { error } = await supabase.auth.signUp({
      email,
      password,
      // profiles 行はこのメタデータを見て DB トリガが作る。
      options: { data: { username, display_name: username } },
    });
    if (error) {
      throw new Error(translateAuthError(error.message));
    }
  }, []);

  const signOut = useCallback(async () => {
    await supabase.auth.signOut();
    // 他人の端末で前の利用者のデータが残らないようにする。
    // clear() ではなく resetQueries() を使う理由は上のコメントを参照。
    await queryClient.resetQueries();
  }, [queryClient]);

  const resetPassword = useCallback(async (email: string) => {
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/settings`,
    });
    if (error) {
      throw new Error(translateAuthError(error.message));
    }
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({
      session,
      profile: profile ?? null,
      loading: restoring || (session !== null && profileLoading),
      // 表示の出し分けにだけ使う。認可の判断は必ずサーバー側で行われる (SPEC 2.1-5)。
      isStaff: profile?.role === 'MODERATOR' || profile?.role === 'ADMIN',
      isAdmin: profile?.role === 'ADMIN',
      signIn,
      signUp,
      signOut,
      resetPassword,
    }),
    [session, profile, restoring, profileLoading, signIn, signUp, signOut, resetPassword],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth は AuthProvider の内側でのみ使えます');
  }
  return context;
}

/** Supabase Auth の英語メッセージを、そのまま出しても意味が通る日本語にする。 */
function translateAuthError(message: string): string {
  const lower = message.toLowerCase();
  if (lower.includes('invalid login credentials')) {
    return 'メールアドレスまたはパスワードが正しくありません';
  }
  if (lower.includes('user already registered')) {
    return 'このメールアドレスはすでに登録されています';
  }
  if (lower.includes('password should be at least')) {
    return 'パスワードは 8 文字以上にしてください';
  }
  if (lower.includes('email rate limit')) {
    return 'メール送信の回数制限に達しました。しばらく待ってください';
  }
  return message;
}
